<template>
  <form v-if="newUiAuthEnabled" class="auth-card__form" @submit.prevent="onSubmit">
    <AuthCardHeader
      :icon="KeyRound"
      :title="$t('Activate account')"
      :subtitle="$t('To activate your account, please set your password.')"
    />

    <AuthPasswordInput
      id="activate-password"
      v-model="form.password"
      :label="$t('Password')"
      :icon="Lock"
      autocomplete="new-password"
      :placeholder="$t('Enter new password...')"
      :disabled="isPending"
      required
      autofocus
    />

    <AuthPasswordInput
      id="activate-password-confirmation"
      v-model="form.passwordConfirmation"
      :label="$t('Repeat password')"
      :icon="Lock"
      autocomplete="new-password"
      :placeholder="$t('Repeat new password...')"
      :disabled="isPending"
      required
    />

    <div class="auth-actions">
      <UiButton
        variant="secondary"
        :icon="ChevronLeft"
        class="auth-actions__back"
        @click="router.push({ name: 'auth.login' })"
      >
        {{ $t("Back to login") }}
      </UiButton>

      <UiButton type="submit" variant="primary" :icon="LockOpen" :loading="isPending">
        {{ $t("Activate") }}
      </UiButton>
    </div>
  </form>

  <b-card v-else class="card-accent-primary p-4" no-body>
    <b-card-body>
      <div v-if="isPending" id="form-loader">
        <Loading />
      </div>

      <b-form :style="{ opacity: isPending ? 0.5 : 1 }" @submit.prevent="onSubmit">
        <h1 class="mb-3"><UiIcon fa="fa fa-unlock-alt me-1" /> {{ $t("Activate account") }}</h1>

        <p class="text-muted">
          {{ $t("To activate your account, please set your password.") }}
        </p>

        <b-input-group class="mb-3">
          <div class="input-group-text">
            <UiIcon fa="fa fa-key" />
          </div>

          <b-form-input
            v-model="form.password"
            :disabled="isPending"
            :placeholder="$t('Password')"
            class="form-control"
            required
            type="password"
            autofocus
          />
        </b-input-group>

        <b-input-group class="mb-3">
          <div class="input-group-text">
            <UiIcon fa="fa fa-key" />
          </div>

          <b-form-input
            v-model="form.passwordConfirmation"
            :disabled="isPending"
            :placeholder="$t('Repeat password')"
            class="form-control"
            required
            type="password"
          />
        </b-input-group>

        <b-row>
          <b-col class="d-flex justify-content-between" sm="12">
            <b-button :to="{ name: 'auth.login' }" variant="secondary">
              <UiIcon fa="fa fa-arrow-left me-1" /> {{ $t("Back to login") }}
            </b-button>

            <b-button :disabled="isPending" type="submit" variant="primary">
              <UiIcon fa="fa fa-unlock me-1" /> {{ $t("Activate") }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card-body>
  </b-card>
</template>

<script lang="ts" setup>
import { useShowNotification } from "@/composables/showNotification.ts";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { usePostActivateGlobalUser } from "@generated/auth/query/auth.ts";
import { useRoute, useRouter } from "vue-router";
import Loading from "@/components/Loading.vue";
import { ChevronLeft, KeyRound, Lock, LockOpen } from "lucide-vue-next";
import AuthCardHeader from "./newUi/AuthCardHeader.vue";
import AuthPasswordInput from "./newUi/AuthPasswordInput.vue";
import { useNewUiEnabled } from "@/components/newUi/core/useNewUiEnabled";

const router = useRouter();
const newUiAuthEnabled = useNewUiEnabled();
const { t } = useI18n();
const { showNotification } = useShowNotification();
const { mutateAsync, isPending } = usePostActivateGlobalUser();
const { params } = useRoute();

const form = ref({
  password: "",
  passwordConfirmation: "",
});

const onSubmit = async () => {
  await mutateAsync({
    token: params.token as string,
    data: {
      password: form.value.password,
      passwordConfirmation: form.value.passwordConfirmation,
    },
  });

  showNotification({
    type: "success",
    text: t("Account has been activated. You can log in using your new password now."),
  });

  await router.push({ name: "auth.login" });
};
</script>

<style scoped>
#form-loader {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  top: 50%;
}
</style>
